import { loadProfileNicknames } from "../../shared/profileNicknames.js";
import { supabase } from "./supabase";
import { getMyDjangoUserId } from "./supabaseCommunity";

/** 송금 후 유지해야 하는 최소 잔액 */
export const MIN_POINTS_RESERVE_AFTER_GIFT = 1000;

function appOrigin(): string {
  return typeof window !== "undefined" ? window.location.origin : "";
}

async function getBearerToken(): Promise<string> {
  if (!supabase) throw new Error("연결을 확인할 수 없습니다.");
  const { data, error } = await supabase.auth.getSession();
  if (error || !data.session?.access_token) {
    throw new Error("로그인이 필요합니다.");
  }
  return data.session.access_token;
}

async function fetchWithSupabaseAuth(url: string, init: RequestInit = {}, isRetry = false): Promise<Response> {
  const token = await getBearerToken();
  const headers = new Headers(init.headers);
  headers.set("Authorization", `Bearer ${token}`);
  if (init.body && !(init.body instanceof FormData) && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  const res = await fetch(url, { ...init, headers });
  if (res.status === 401 && !isRetry && supabase) {
    const { data } = await supabase.auth.refreshSession();
    if (data.session?.access_token) {
      return fetchWithSupabaseAuth(url, init, true);
    }
  }
  return res;
}

export type GiftPointsResponse = {
  success: boolean;
  gifted: number;
  remaining_points: number | null;
  recipient_points?: number | null;
};

export async function giftPointsToUser(payload: {
  recipientUserId: number;
  amount: number;
}): Promise<GiftPointsResponse> {
  const res = await fetchWithSupabaseAuth(`${appOrigin()}/api/points/gift`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const j = await res.json().catch(() => ({}));
    throw new Error((j as { error?: string }).error || "포인트 선물에 실패했습니다.");
  }
  return res.json() as Promise<GiftPointsResponse>;
}

// ─── 타입 정의 ─────────────────────────────────────────────────────────────

export interface PointHistoryItem {
  id: number;
  points: number;
  description: string;
  created_at: string;
}

export interface LeaderboardEntry {
  nickname?: string | null;
  user_id: number;
  username: string;
  activity_points: number;
  rank: number;
}

export interface LotteryResult {
  points: number;
  already_drawn: boolean;
}

export interface AttendanceResult {
  streak: number;
  monthly_count: number;
  bonus_awarded: number;
  attendance_dates: string[];
  already_checked: boolean;
}

// ─── 포인트 잔액 ──────────────────────────────────────────────────────────

export async function getMyPoints(): Promise<number | null> {
  if (!supabase) return null;
  const myUserId = await getMyDjangoUserId();
  if (!myUserId) return null;

  const { data, error } = await supabase
    .from("common_profile")
    .select("activity_points")
    .eq("user_id", myUserId)
    .maybeSingle();

  if (error || !data) {
    console.error("getMyPoints error:", error);
    return null;
  }
  return data.activity_points as number;
}

// ─── 포인트 이력 ──────────────────────────────────────────────────────────

export async function getMyPointHistory(limit = 10): Promise<PointHistoryItem[]> {
  if (!supabase) return [];
  const myUserId = await getMyDjangoUserId();
  if (!myUserId) return [];

  const { data, error } = await supabase
    .from("common_pointhistory")
    .select("id, points, description, created_at")
    .eq("user_id", myUserId)
    .order("created_at", { ascending: false })
    .limit(limit);

  if (error || !data) {
    console.error("getMyPointHistory error:", error);
    return [];
  }
  return data as PointHistoryItem[];
}

// ─── 일일 랜덤 뽑기 ────────────────────────────────────────────────────────

export async function drawDailyLottery(): Promise<LotteryResult | null> {
  if (!supabase) return null;
  const myUserId = await getMyDjangoUserId();
  if (!myUserId) return null;

  const { data, error } = await supabase.rpc("daily_lottery_rpc", { p_user_id: myUserId });

  if (error) {
    console.error("drawDailyLottery error:", error);
    return null;
  }
  return data as LotteryResult;
}

export function getKSTDateString(): string {
  // 한국시간(UTC+9) 기준 오늘 날짜를 YYYY-MM-DD 형식으로 반환
  const now = new Date();
  const kst = new Date(now.getTime() + 9 * 60 * 60 * 1000);
  return kst.toISOString().split("T")[0];
}

export async function getTodayLottery(): Promise<LotteryResult | null> {
  if (!supabase) return null;
  const myUserId = await getMyDjangoUserId();
  if (!myUserId) return null;

  const today = getKSTDateString();
  const { data, error } = await supabase
    .from("common_daily_lottery")
    .select("points")
    .eq("user_id", myUserId)
    .eq("date", today)
    .maybeSingle();

  if (error) {
    console.error("getTodayLottery error:", error);
    return null;
  }
  if (!data) return null;
  return { points: (data as any).points, already_drawn: true };
}

// ─── 출석 체크 ─────────────────────────────────────────────────────────────

export async function checkAttendance(): Promise<AttendanceResult | null> {
  if (!supabase) return null;
  const myUserId = await getMyDjangoUserId();
  if (!myUserId) return null;

  const { data, error } = await supabase.rpc("check_attendance_rpc", { p_user_id: myUserId });

  if (error) {
    console.error("checkAttendance error:", error);
    return null;
  }
  return data as AttendanceResult;
}

const ATTENDANCE_LOCAL_KEY = "fe_attendance_kst";

/** 로그인 셸에서 하루 1회 출석 RPC 호출 (KST localStorage 가드). */
export async function ensureDailyAttendance(): Promise<AttendanceResult | null> {
  if (typeof window === "undefined") return null;
  const today = getKSTDateString();
  try {
    if (window.localStorage.getItem(ATTENDANCE_LOCAL_KEY) === today) return null;
  } catch {
    // localStorage 불가 환경은 RPC만 시도
  }

  const result = await checkAttendance();
  if (result) {
    try {
      window.localStorage.setItem(ATTENDANCE_LOCAL_KEY, today);
    } catch {
      // ignore
    }
  }
  return result;
}

export async function getMonthlyAttendance(): Promise<string[]> {
  if (!supabase) return [];
  const myUserId = await getMyDjangoUserId();
  if (!myUserId) return [];

  // 한국시간(KST) 기준으로 이번 달 범위 계산
  const nowKST = new Date(Date.now() + 9 * 60 * 60 * 1000);
  const firstDay = new Date(nowKST.getUTCFullYear(), nowKST.getUTCMonth(), 1)
    .toISOString().split("T")[0];
  const today = getKSTDateString();

  const { data, error } = await supabase
    .from("common_attendance")
    .select("date")
    .eq("user_id", myUserId)
    .gte("date", firstDay)
    .lte("date", today)
    .order("date", { ascending: true });

  if (error || !data) return [];
  return data.map((row: any) => row.date as string);
}

// ─── 포인트 랭킹 ──────────────────────────────────────────────────────────

export async function getLeaderboard(limit = 10): Promise<LeaderboardEntry[]> {
  if (!supabase) return [];

  const { data, error } = await supabase
    .from("point_leaderboard_view")
    .select("user_id, username, activity_points, rank")
    .order("rank", { ascending: true })
    .limit(limit);

  if (error || !data) {
    console.error("getLeaderboard error:", error);
    return [];
  }

  const entries = data as LeaderboardEntry[];

  try {
    const names = await loadProfileNicknames(supabase, entries.map((entry) => entry.user_id));
    return entries.map((entry) => ({ ...entry, nickname: names.get(entry.user_id) ?? null }));
  } catch {
    return entries.map((entry) => ({ ...entry, username: "", nickname: null }));
  }
}

export async function getMyRank(): Promise<number | null> {
  if (!supabase) return null;
  const myUserId = await getMyDjangoUserId();
  if (!myUserId) return null;

  const { data, error } = await supabase
    .from("point_leaderboard_view")
    .select("rank")
    .eq("user_id", myUserId)
    .maybeSingle();

  if (error || !data) return null;
  return (data as any).rank as number;
}

// ─── 슬롯 구매 ────────────────────────────────────────────────────────────

export interface PurchaseSlotResult {
  success: boolean;
  reason?: "insufficient_points" | "profile_not_found";
  current_points?: number;
  remaining_points?: number;
}

export async function purchaseExtraSlot(
  feature:
    | "registry"
    | "progress"
    | "timetable_school"
    | "receipts"
    | "essay_grading"
    | "consent_form"
    | "lost_found"
    | "document_sign"
    | "office_builder"
    | "collect"
    | "training_certs"
    | "equipment_loan"
    | "file_inbox"
    | "word_cloud"
    | "onepick",
  cost = 500
): Promise<PurchaseSlotResult> {
  if (!supabase) return { success: false, reason: "profile_not_found" };
  const myUserId = await getMyDjangoUserId();
  if (!myUserId) return { success: false, reason: "profile_not_found" };

  const { data, error } = await supabase.rpc("purchase_extra_slot_rpc", {
    p_user_id: myUserId,
    p_feature: feature,
    p_cost: cost,
  });

  if (error) {
    console.error("purchaseExtraSlot error:", error);
    return { success: false, reason: "profile_not_found" };
  }
  return data as PurchaseSlotResult;
}
